import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import {
  Search,
  Filter,
  Download,
  RefreshCw,
  Eye,
  X,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Activity,
} from 'lucide-react';
import { format, subDays } from 'date-fns';

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = ['AUTH', 'MEETING', 'SPEAKER', 'USER', 'ADMIN', 'RECORDING'];

const ACTION_LABELS = {
  AUTH_LOGIN_SUCCESS:           'Login – Success',
  AUTH_LOGIN_FAILED:            'Login – Failed',
  AUTH_LOGOUT:                  'Logout',
  AUTH_REGISTER:                'Registration',
  AUTH_PASSWORD_CHANGED:        'Password Changed',
  AUTH_PASSWORD_RESET_REQUEST:  'Password Reset Request',
  AUTH_PASSWORD_RESET_COMPLETE: 'Password Reset Complete',
  MEETING_CREATED:              'Meeting Created',
  MEETING_UPDATED:              'Meeting Updated',
  MEETING_STARTED:              'Meeting Started',
  MEETING_ENDED:                'Meeting Ended',
  MEETING_CANCELLED:            'Meeting Cancelled',
  MEETING_ARCHIVED:             'Meeting Archived',
  MEETING_VISIBILITY_TOGGLED:   'Meeting Visibility Toggled',
  MEETING_ARCHIVE_LINK_SET:     'Archive Link Set',
  MEETING_JOINED:               'Meeting Joined',
  MEETING_JOIN_DENIED:          'Meeting Join Denied',
  MEETING_STREAM_ACCESSED:      'Stream Accessed',
  SPEAKER_CREATED:              'Speaker Created',
  SPEAKER_UPDATED:              'Speaker Updated',
  SPEAKER_ACTIVATED:            'Speaker Activated (Global)',
  SPEAKER_DEACTIVATED:          'Speaker Deactivated (Global)',
  SPEAKER_ASSIGNED_MEETING:     'Speaker Assigned to Meeting',
  SPEAKER_MEETING_ACTIVATED:    'Speaker Active in Meeting',
  SPEAKER_MEETING_DEACTIVATED:  'Speaker Inactive in Meeting',
  USER_ACTIVATED:               'User Activated',
  USER_DEACTIVATED:             'User Deactivated',
  ADMIN_CREATED:                'Admin Created',
  ADMIN_UPDATED:                'Admin Updated',
  ADMIN_ACTIVATED:              'Admin Activated',
  ADMIN_DEACTIVATED:            'Admin Deactivated',
  RECORDING_ACCESSED:           'Recording Accessed',
};

const CATEGORY_BADGE = {
  AUTH:      'info',
  MEETING:   'primary',
  SPEAKER:   'warning',
  USER:      'success',
  ADMIN:     'danger',
  RECORDING: 'default',
};

const OUTCOME_BADGE = {
  SUCCESS: 'success',
  FAILED:  'danger',
  DENIED:  'warning',
};

const ROLE_LABELS = {
  ADMIN:       'Admin',
  SUPER_ADMIN: 'Super Admin',
  SPEAKER:     'Speaker',
  USER:        'User',
};

const DATE_PRESETS = [
  { label: 'Today',      days: 0 },
  { label: 'Last 7 days',  days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

const ACTIONS_BY_CATEGORY = {
  AUTH: Object.keys(ACTION_LABELS).filter((k) => k.startsWith('AUTH_')),
  MEETING: Object.keys(ACTION_LABELS).filter((k) => k.startsWith('MEETING_')),
  SPEAKER: Object.keys(ACTION_LABELS).filter((k) => k.startsWith('SPEAKER_')),
  USER: Object.keys(ACTION_LABELS).filter((k) => k.startsWith('USER_')),
  ADMIN: Object.keys(ACTION_LABELS).filter((k) => k.startsWith('ADMIN_')),
  RECORDING: Object.keys(ACTION_LABELS).filter((k) => k.startsWith('RECORDING_')),
};

// ─── CSV Export helper ────────────────────────────────────────────────────────

function toCSV(rows) {
  if (!rows.length) return '';
  const cols = [
    'occurredAt', 'category', 'action', 'outcome', 'severity',
    'actorName', 'actorEmail', 'actorRole',
    'subjectType', 'subjectLabel',
    'meetingTitle',
    'targetName',
    'reasonCode',
    'ipAddress', 'userAgent',
    'metadata',
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const header = cols.join(',');
  const body = rows.map((r) => cols.map((c) => escape(r[c])).join(',')).join('\n');
  return `${header}\n${body}`;
}

function downloadCSV(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── Detail Drawer ────────────────────────────────────────────────────────────

const DetailDrawer = ({ event, onClose }) => {
  const { theme } = useTheme();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!event) return;
    setLoading(true);
    api.get(`/admin/audit-events/${event.id}`)
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(event))
      .finally(() => setLoading(false));
  }, [event]);

  if (!event) return null;

  const row = (label, value) => {
    if (!value && value !== 0) return null;
    return (
      <div key={label} style={{ display: 'flex', gap: '12px', padding: '10px 0', borderBottom: `1px solid ${theme.colors.border}` }}>
        <div style={{ width: '160px', flexShrink: 0, fontSize: '12px', color: theme.colors.textSecondary, fontWeight: '500', paddingTop: '1px' }}>
          {label}
        </div>
        <div style={{ flex: 1, fontSize: '13px', color: theme.colors.text, wordBreak: 'break-word' }}>
          {value}
        </div>
      </div>
    );
  };

  const d = detail || event;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', justifyContent: 'flex-end',
    }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)' }} />

      {/* Panel */}
      <div style={{
        position: 'relative', zIndex: 1, width: '520px', maxWidth: '95vw',
        backgroundColor: theme.colors.cardBg, height: '100%', overflowY: 'auto',
        boxShadow: theme.shadows['2xl'], display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${theme.colors.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: theme.colors.text }}>
              Event Details
            </div>
            <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginTop: '2px', fontFamily: 'monospace' }}>
              {d.id}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.textSecondary, padding: '4px' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', flex: 1 }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {/* Action + outcome prominent */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <Badge variant={CATEGORY_BADGE[d.category] || 'default'} size="md">
                  {d.category}
                </Badge>
                <Badge variant={OUTCOME_BADGE[d.outcome] || 'default'} size="md" status>
                  {d.outcome}
                </Badge>
                {d.severity !== 'INFO' && (
                  <Badge variant={d.severity === 'ERROR' ? 'danger' : 'warning'} size="md" status>
                    {d.severity}
                  </Badge>
                )}
              </div>

              <div style={{ fontSize: '15px', fontWeight: '600', color: theme.colors.text, marginBottom: '20px' }}>
                {ACTION_LABELS[d.action] || d.action}
              </div>

              {/* Rows */}
              <div>
                {row('Occurred At', d.occurredAt ? format(new Date(d.occurredAt), 'MMM d, yyyy  h:mm:ss a') : null)}
                {row('Actor', d.actorName ? `${d.actorName} (${d.actorEmail || ''})` : null)}
                {row('Actor Role', d.actorRole ? (ROLE_LABELS[d.actorRole] || d.actorRole) : null)}
                {row('Subject Type', d.subjectType)}
                {row('Subject', d.subjectLabel)}
                {row('Meeting', d.meetingTitle)}
                {row('Target User', d.targetName)}
                {row('Reason Code', d.reasonCode)}
                {row('IP Address', d.ipAddress)}
                {row('User Agent', d.userAgent)}
              </div>

              {/* Metadata block */}
              {d.metadata && Object.keys(d.metadata).length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: theme.colors.textSecondary, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Additional Context
                  </div>
                  <div style={{
                    backgroundColor: theme.colors.backgroundSecondary,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px', padding: '14px',
                    fontFamily: 'monospace', fontSize: '12px',
                    color: theme.colors.text, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  }}>
                    {JSON.stringify(d.metadata, null, 2)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const AuditLogs = () => {
  const { theme } = useTheme();

  // ── Events list ──────────────────────────────────────────────────────────
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ── Filters ──────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [action, setAction] = useState('');
  const [outcome, setOutcome] = useState('');
  const [actorRole, setActorRole] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 50;

  // ── Detail drawer ────────────────────────────────────────────────────────
  const [selectedEvent, setSelectedEvent] = useState(null);

  // ── Export state ─────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);

  // ── Filter panel open ────────────────────────────────────────────────────
  const [filterOpen, setFilterOpen] = useState(false);

  const searchTimeout = useRef(null);

  // ── Build query params from current filter state ─────────────────────────
  const buildParams = useCallback((overrides = {}) => {
    const p = { page, limit: LIMIT };
    if (search.trim())  p.search    = search.trim();
    if (category)       p.category  = category;
    if (action)         p.action    = action;
    if (outcome)        p.outcome   = outcome;
    if (actorRole)      p.actorRole = actorRole;
    if (dateFrom)       p.dateFrom  = dateFrom;
    if (dateTo)         p.dateTo    = dateTo;
    return { ...p, ...overrides };
  }, [page, search, category, action, outcome, actorRole, dateFrom, dateTo]);

  // ── Fetch events ──────────────────────────────────────────────────────────
  const fetchEvents = useCallback(() => {
    setLoading(true);
    setError('');
    api.get('/admin/audit-events', { params: buildParams() })
      .then((r) => {
        setEvents(r.data.events || []);
        setTotal(r.data.total || 0);
        setTotalPages(r.data.totalPages || 1);
      })
      .catch(() => setError('Failed to load audit events.'))
      .finally(() => setLoading(false));
  }, [buildParams]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // ── Debounce search ────────────────────────────────────────────────────────
  const handleSearchChange = (v) => {
    setSearch(v);
    setPage(1);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(fetchEvents, 400);
  };

  // ── Date preset ───────────────────────────────────────────────────────────
  const applyDatePreset = (days) => {
    if (days === 0) {
      const today = format(new Date(), 'yyyy-MM-dd');
      setDateFrom(today);
      setDateTo(today);
    } else {
      setDateFrom(format(subDays(new Date(), days), 'yyyy-MM-dd'));
      setDateTo(format(new Date(), 'yyyy-MM-dd'));
    }
    setPage(1);
  };

  const clearFilters = () => {
    setSearch(''); setCategory(''); setAction(''); setOutcome('');
    setActorRole(''); setDateFrom(''); setDateTo('');
    setDatePreset(''); setPage(1);
  };

  const hasFilters = search || category || action || outcome || actorRole || dateFrom || dateTo;

  // ── Export ────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = buildParams({ page: undefined, limit: undefined });
      const r = await api.get('/admin/audit-events/export', { params });
      const rows = r.data.events || [];
      const csv = toCSV(rows);
      const ts = format(new Date(), 'yyyy-MM-dd_HH-mm');
      downloadCSV(csv, `activity-logs-${ts}.csv`);
    } catch {
      // silent
    } finally {
      setExporting(false);
    }
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const s = {
    container: { padding: '32px', maxWidth: '1400px', margin: '0 auto' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
    filterBar: {
      backgroundColor: theme.colors.cardBg, border: `1px solid ${theme.colors.border}`,
      borderRadius: '12px', padding: '16px 20px', marginBottom: '16px',
    },
    filterRow: { display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '140px' },
    filterLabel: { fontSize: '11px', fontWeight: '600', color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: {
      padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
      border: `1px solid ${theme.colors.border}`, backgroundColor: theme.colors.inputBg,
      color: theme.colors.text, outline: 'none', width: '100%',
    },
    select: {
      padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
      border: `1px solid ${theme.colors.border}`, backgroundColor: theme.colors.inputBg,
      color: theme.colors.text, cursor: 'pointer', outline: 'none',
    },
    tableCard: {
      backgroundColor: theme.colors.cardBg, border: `1px solid ${theme.colors.border}`,
      borderRadius: '12px', overflow: 'hidden',
    },
    tableHeader: {
      padding: '14px 20px', borderBottom: `1px solid ${theme.colors.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      backgroundColor: theme.colors.backgroundSecondary,
    },
    th: {
      padding: '10px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '600',
      color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px',
      whiteSpace: 'nowrap',
    },
    td: {
      padding: '12px 16px', fontSize: '13px', color: theme.colors.text,
      borderBottom: `1px solid ${theme.colors.border}`,
    },
    pagination: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 20px', borderTop: `1px solid ${theme.colors.border}`,
      flexWrap: 'wrap', gap: '10px',
    },
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={s.container}>
      {/* ── Header ── */}
      <div style={s.header}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <Button
            size="sm" variant="outline"
            icon={RefreshCw}
            onClick={fetchEvents}
          >
            Refresh
          </Button>
          <Button
            size="sm" variant="outline"
            icon={Download}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div style={s.filterBar}>
        {/* Search + toggle */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: filterOpen ? '14px' : '0', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '1', minWidth: '200px', position: 'relative' }}>
            <Search size={15} color={theme.colors.textSecondary} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              style={{ ...s.input, paddingLeft: '32px' }}
              placeholder="Search by actor, meeting, action…"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>

          {/* Date presets */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {DATE_PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => { setDatePreset(p.label); applyDatePreset(p.days); }}
                style={{
                  padding: '7px 12px', fontSize: '12px', fontWeight: '500',
                  borderRadius: '8px', border: `1px solid ${datePreset === p.label ? theme.colors.primary : theme.colors.border}`,
                  backgroundColor: datePreset === p.label ? `${theme.colors.primary}15` : theme.colors.inputBg,
                  color: datePreset === p.label ? theme.colors.primary : theme.colors.textSecondary,
                  cursor: 'pointer',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setFilterOpen((v) => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
              border: `1px solid ${filterOpen ? theme.colors.primary : theme.colors.border}`,
              backgroundColor: filterOpen ? `${theme.colors.primary}15` : theme.colors.inputBg,
              color: filterOpen ? theme.colors.primary : theme.colors.textSecondary,
              cursor: 'pointer',
            }}
          >
            <Filter size={14} />
            Filters
            {hasFilters && <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: theme.colors.error, display: 'inline-block' }} />}
          </button>

          {hasFilters && (
            <button
              onClick={clearFilters}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 12px',
                borderRadius: '8px', border: 'none', backgroundColor: `${theme.colors.error}12`,
                color: theme.colors.error, cursor: 'pointer', fontSize: '12px', fontWeight: '500',
              }}
            >
              <X size={13} /> Clear
            </button>
          )}
        </div>

        {/* Expanded filters */}
        {filterOpen && (
          <div style={s.filterRow}>
            <div style={s.filterGroup}>
              <span style={s.filterLabel}>Category</span>
              <select style={s.select} value={category} onChange={(e) => { setCategory(e.target.value); setAction(''); setPage(1); }}>
                <option value="">All categories</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div style={s.filterGroup}>
              <span style={s.filterLabel}>Action</span>
              <select style={s.select} value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}>
                <option value="">All actions</option>
                {(category ? (ACTIONS_BY_CATEGORY[category] || []) : Object.keys(ACTION_LABELS)).map((a) => (
                  <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
                ))}
              </select>
            </div>

            <div style={s.filterGroup}>
              <span style={s.filterLabel}>Outcome</span>
              <select style={s.select} value={outcome} onChange={(e) => { setOutcome(e.target.value); setPage(1); }}>
                <option value="">All outcomes</option>
                <option value="SUCCESS">Success</option>
                <option value="FAILED">Failed</option>
                <option value="DENIED">Denied</option>
              </select>
            </div>

            <div style={s.filterGroup}>
              <span style={s.filterLabel}>Actor Role</span>
              <select style={s.select} value={actorRole} onChange={(e) => { setActorRole(e.target.value); setPage(1); }}>
                <option value="">All roles</option>
                <option value="ADMIN">Admin</option>
                <option value="SUPER_ADMIN">Super Admin</option>
                <option value="SPEAKER">Speaker</option>
                <option value="USER">User</option>
              </select>
            </div>

            <div style={s.filterGroup}>
              <span style={s.filterLabel}>From Date</span>
              <input type="date" style={s.input} value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setDatePreset(''); setPage(1); }} />
            </div>

            <div style={s.filterGroup}>
              <span style={s.filterLabel}>To Date</span>
              <input type="date" style={s.input} value={dateTo} onChange={(e) => { setDateTo(e.target.value); setDatePreset(''); setPage(1); }} />
            </div>
          </div>
        )}
      </div>

      {/* ── Table ── */}
      <div style={s.tableCard}>
        <div style={s.tableHeader}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: theme.colors.text }}>
            {loading ? 'Loading…' : `${total.toLocaleString()} event${total !== 1 ? 's' : ''}`}
          </div>
          <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
            Page {page} of {totalPages}
          </div>
        </div>

        {error && (
          <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '8px', color: theme.colors.error, fontSize: '13px' }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: '60px', display: 'flex', justifyContent: 'center' }}>
            <LoadingSpinner />
          </div>
        ) : events.length === 0 ? (
          <EmptyState
            title="No events found"
            description={hasFilters ? 'Try adjusting or clearing your filters.' : 'Activity events will appear here as actions are performed.'}
            icon={Activity}
          />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: theme.colors.backgroundSecondary }}>
                <tr>
                  <th style={s.th}>Time</th>
                  <th style={s.th}>Category</th>
                  <th style={s.th}>Action</th>
                  <th style={s.th}>Actor</th>
                  <th style={s.th}>Subject / Meeting</th>
                  <th style={s.th}>Outcome</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Detail</th>
                </tr>
              </thead>
              <tbody>
                {events.map((evt) => (
                  <tr
                    key={evt.id}
                    style={{ transition: 'background 0.1s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${theme.colors.backgroundSecondary}80`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                  >
                    {/* Time */}
                    <td style={s.td}>
                      <div style={{ fontWeight: '500', whiteSpace: 'nowrap' }}>
                        {format(new Date(evt.occurredAt), 'MMM d, yyyy')}
                      </div>
                      <div style={{ fontSize: '11px', color: theme.colors.textSecondary }}>
                        {format(new Date(evt.occurredAt), 'h:mm:ss a')}
                      </div>
                    </td>

                    {/* Category */}
                    <td style={s.td}>
                      <Badge variant={CATEGORY_BADGE[evt.category] || 'default'} size="sm">
                        {evt.category}
                      </Badge>
                    </td>

                    {/* Action */}
                    <td style={{ ...s.td, maxWidth: '200px' }}>
                      <div style={{ fontWeight: '500', fontSize: '13px' }}>
                        {ACTION_LABELS[evt.action] || evt.action}
                      </div>
                      {evt.reasonCode && (
                        <div style={{ fontSize: '11px', color: theme.colors.textSecondary, marginTop: '2px' }}>
                          {evt.reasonCode}
                        </div>
                      )}
                    </td>

                    {/* Actor */}
                    <td style={s.td}>
                      {evt.actorName ? (
                        <>
                          <div style={{ fontWeight: '500' }}>{evt.actorName}</div>
                          <div style={{ fontSize: '11px', color: theme.colors.textSecondary }}>
                            {evt.actorRole ? (ROLE_LABELS[evt.actorRole] || evt.actorRole) : ''}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: theme.colors.textTertiary, fontSize: '12px' }}>System</span>
                      )}
                    </td>

                    {/* Subject / Meeting */}
                    <td style={{ ...s.td, maxWidth: '180px' }}>
                      {evt.meetingTitle ? (
                        <>
                          <div style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {evt.meetingTitle}
                          </div>
                          {evt.subjectLabel && evt.subjectLabel !== evt.meetingTitle && (
                            <div style={{ fontSize: '11px', color: theme.colors.textSecondary }}>{evt.subjectLabel}</div>
                          )}
                        </>
                      ) : evt.subjectLabel ? (
                        <>
                          <div style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{evt.subjectLabel}</div>
                          {evt.subjectType && <div style={{ fontSize: '11px', color: theme.colors.textSecondary }}>{evt.subjectType}</div>}
                        </>
                      ) : evt.targetName ? (
                        <div style={{ fontSize: '13px', color: theme.colors.textSecondary }}>→ {evt.targetName}</div>
                      ) : (
                        <span style={{ color: theme.colors.textTertiary, fontSize: '12px' }}>—</span>
                      )}
                    </td>

                    {/* Outcome */}
                    <td style={s.td}>
                      <Badge variant={OUTCOME_BADGE[evt.outcome] || 'default'} size="sm" status>
                        {evt.outcome === 'SUCCESS' ? (
                          <><CheckCircle size={10} /> {evt.outcome}</>
                        ) : (
                          <><AlertCircle size={10} /> {evt.outcome}</>
                        )}
                      </Badge>
                    </td>

                    {/* View */}
                    <td style={{ ...s.td, textAlign: 'center' }}>
                      <button
                        onClick={() => setSelectedEvent(evt)}
                        title="View full details"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: theme.colors.primary, padding: '6px',
                          borderRadius: '6px', display: 'inline-flex',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${theme.colors.primary}15`; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div style={s.pagination}>
            <div style={{ fontSize: '13px', color: theme.colors.textSecondary }}>
              Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total.toLocaleString()}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px', padding: '7px 12px',
                  borderRadius: '8px', border: `1px solid ${theme.colors.border}`,
                  backgroundColor: page === 1 ? theme.colors.backgroundSecondary : theme.colors.inputBg,
                  color: page === 1 ? theme.colors.textTertiary : theme.colors.text,
                  cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: '13px',
                }}
              >
                <ChevronLeft size={14} /> Prev
              </button>

              {/* Page number pills */}
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                let p;
                if (totalPages <= 7) {
                  p = i + 1;
                } else if (page <= 4) {
                  p = i + 1;
                } else if (page >= totalPages - 3) {
                  p = totalPages - 6 + i;
                } else {
                  p = page - 3 + i;
                }
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    style={{
                      width: '34px', height: '34px', borderRadius: '8px', fontSize: '13px',
                      border: `1px solid ${p === page ? theme.colors.primary : theme.colors.border}`,
                      backgroundColor: p === page ? theme.colors.primary : theme.colors.inputBg,
                      color: p === page ? '#fff' : theme.colors.text,
                      cursor: 'pointer', fontWeight: p === page ? '700' : '400',
                    }}
                  >
                    {p}
                  </button>
                );
              })}

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px', padding: '7px 12px',
                  borderRadius: '8px', border: `1px solid ${theme.colors.border}`,
                  backgroundColor: page === totalPages ? theme.colors.backgroundSecondary : theme.colors.inputBg,
                  color: page === totalPages ? theme.colors.textTertiary : theme.colors.text,
                  cursor: page === totalPages ? 'not-allowed' : 'pointer', fontSize: '13px',
                }}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Detail drawer ── */}
      {selectedEvent && (
        <DetailDrawer event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </div>
  );
};

export default AuditLogs;
